import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import classNames from 'classnames';
import { FormattedMessage, intlShape, injectIntl } from '../../util/reactIntl';
import { createResourceLocatorString, findRouteByRouteName } from '../../util/routes';
import routeConfiguration from '../../routeConfiguration';
import { createSlug } from '../../util/urlHelpers';
import { propTypes, LINE_ITEM_NIGHT } from '../../util/types';
import { ensureCurrentUser, ensureListing, ensureTransaction, ensureUser } from '../../util/data';
import { getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { isScrollingDisabled } from '../../ducks/UI.duck';
import css from './TransactionPage.css';
import {
    NamedRedirect,
    TransactionPanel,
    Page,
    LayoutSingleColumn,
    LayoutWrapperTopbar,
    LayoutWrapperMain,
    LayoutWrapperFooter,
    IconSpinner,
} from '../../components';
import { loadData, setInitialValues } from './TransactionPage.duck';
import { TopbarContainer } from '../../containers';
import { types as sdkTypes } from '../../util/sdkLoader';
import { useListings } from '../../hooks/useListings.js';
import { useIsMobile } from '../../hooks/useIsMobile.js';
import InboxPanel from './InboxPanel';
import AsidePanel from './AsidePanel';
import { useMatchingMatrix } from '../../hooks/useMatchingMatrix';
import { useRiderListing } from '../../hooks/useRiderListing';
import { anonymizeOtherParty } from '../../util/anonym';
import { resolveOtherPartyData } from '../../util/transaction';
import config from '../../config';
import { sendMessage } from '../InboxPage/InboxPage.duck';

const { userTypeRider, userTypeHorseowner } = config;
const { UUID } = sdkTypes;

// TransactionPage handles data loading for Sale and Order views to transaction pages in Inbox.
export const TransactionPageComponent = props => {
    const {
        currentUser,
        initialMessageFailedToTransaction,
        transactions,
        history,
        intl,
        params,
        scrollingDisabled,
        callSetInitialValues,
        fetchInProgress,
        fetchOrdersOrSalesError,
        archievedTransactionsLoading,
        allUserMessages,
        ...rest
    } = props;
    const [transactionError, setTransactionError] = useState(null);
    const [asidePanelVisible, setAsidePanelVisibility] = useState(false);
    const [inboxPanelVisible, setInboxPanelVisible] = useState(false);
    const [transactionPanelVisible, setTransactionPanelVisible] = useState(false);
    const [isMobile, , computing] = useIsMobile(1024);

    const pageId = params.id;
    const isDetailsPage = !!pageId;
    const transaction = (transactions || []).find(({ id: { uuid } }) => uuid === pageId);

    const currentTransaction = ensureTransaction(transaction);
    const currentCustomer = ensureUser(currentTransaction.customer);
    const currentProvider = ensureUser(currentTransaction.provider);

    const isProviderRole = currentProvider?.id?.uuid === currentUser?.id?.uuid;
    const isCustomerRole = currentCustomer?.id?.uuid === currentUser?.id?.uuid;
    /**
     * in case an owner requests a rider,
     * he becomes a customer, and his previously
     * selected horse listing's id is set as
     * listingSubstitutionId
     */
    const { listingSubstitutionId } = currentTransaction.attributes.protectedData || {};

    const {
        id: { uuid: currentUserId },
        attributes: {
            emailVerified,
            profile: {
                publicData: { userType: currentUserType },
            },
        },
    } = currentUser;

    const currentUserIsRider = currentUserType === userTypeRider; // customer
    const currentUserIsHorseowner = currentUserType === userTypeHorseowner; // customer

    const authorId =
        listingSubstitutionId && currentTransaction.customer.id
            ? currentTransaction.customer.id.uuid
            : null;

    const deletedListingTitle = intl.formatMessage({
        id: 'TransactionPage.deletedListing',
    });

    const [allOwnerListings, listingsReqInProgress] = useListings({
        params: { authorId },
        allowed: !!authorId,
    });

    const isOwnerListing =
        listingSubstitutionId && Array.isArray(allOwnerListings) && allOwnerListings.length > 0;
    const horseownerListing =
        isOwnerListing &&
        allOwnerListings.find(({ id: { uuid } }) => uuid === listingSubstitutionId);

    const currentListing = ensureListing(horseownerListing || currentTransaction.listing);
    const currentListingId = currentListing?.id?.uuid;
    const listingIsDeleted = currentListing?.attributes?.deleted;

    const otherParty = anonymizeOtherParty(
        resolveOtherPartyData(currentUser, currentCustomer, currentProvider),
        currentUser,
        currentListing
    );

    useEffect(() => {
        if (fetchInProgress) return;

        if (isDetailsPage && !transaction && !fetchInProgress) {
            return setTransactionError('fetchFailed');
        }

        setTransactionError(null);
    }, [transaction, fetchInProgress, isDetailsPage]);

    useEffect(() => {
        if (computing) return;

        const changePanelsStates = (inbox, tx, aside) => {
            setInboxPanelVisible(inbox);
            setTransactionPanelVisible(tx);
            setAsidePanelVisibility(aside);
        };

        if (!isDetailsPage) {
            /**
             * for message foute only inbox is visible
             */
            return changePanelsStates(true, false, false);
        }
        if (isDetailsPage && !isMobile) {
            /**
             * for desktop inbox & tx sections are visible
             */
            return changePanelsStates(true, true, false);
        }
        if (isDetailsPage && isMobile) {
            /**
             * for mob details tx section is visible
             */
            return changePanelsStates(false, true, false);
        }
    }, [isDetailsPage, isMobile, computing]);

    useEffect(() => {
        if (computing) return;
        if (isMobile) return;
        if (!currentListingId) return;
        if (listingIsDeleted) return;
        // if (!currentListingId || currentTransaction.listing.attributes.deleted) return;

        setAsidePanelVisibility(true);
    }, [isMobile, computing, currentListingId, listingIsDeleted]);
    /**
     * Close the aside panel upon navigating to another chat page
     */
    useEffect(() => {
        if (!currentListingId) {
            setAsidePanelVisibility(false);
        }
    }, [currentListingId]);
    /**
     * Close the aside panel upon navigating to another chat page
     */
    useEffect(() => {
        if (listingIsDeleted) {
            setAsidePanelVisibility(false);
        }
    }, [listingIsDeleted]);

    const riderId = currentUserIsRider ? currentUserId : otherParty?.id?.uuid;
    /* useEffect */
    const [riderListing, riderListingsRequestInProgress] = useRiderListing({
        params: { authorId: riderId },
    });

    /* useEffect */
    const [matchingMatrixLoading, matchingScore] = useMatchingMatrix({
        riderListingId: riderListing && riderListing.id ? riderListing.id.uuid : null,
        horseOwnerListingId: currentListingId,
    });

    const matchingViewerListing = currentUserIsHorseowner
        ? horseownerListing || currentListing
        : riderListing;
    const matchingCurrentPageListing = currentUserIsHorseowner
        ? riderListing
        : horseownerListing || currentListing;

    const handleSubmitSubscriptionRequest = values => {
        const { bookingDates } = values;

        const initialValues = {
            listing: currentListing,
            transaction: currentTransaction,
            booking: {
                id: new UUID('estimated-booking'),
                type: 'booking',
                attributes: {
                    start: bookingDates.startDate,
                    end: bookingDates.endDate,
                },
            },
            bookingData: {
                unitType: LINE_ITEM_NIGHT,
                quantity: 1,
                startDate: bookingDates.startDate,
                endDate: bookingDates.endDate,
                unitPrice: currentListing.attributes.price,
            },
            bookingDates: {
                bookingStart: bookingDates.startDate,
                bookingEnd: bookingDates.endDate,
            },
        };

        const routes = routeConfiguration();
        const { setInitialValues } = findRouteByRouteName('CheckoutPage', routes);

        callSetInitialValues(setInitialValues, initialValues);

        history.push(
            createResourceLocatorString(
                'CheckoutPage',
                routes,
                { id: currentListing.id.uuid, slug: createSlug(currentListing) },
                {}
            )
        );
    };

    const listingTitle = currentListing
        ? listingIsDeleted || !currentListingId
            ? deletedListingTitle
            : currentListing.attributes.title
        : null;

    const isLoading = fetchInProgress || listingsReqInProgress || archievedTransactionsLoading;

    // Redirect users with someone else's direct link to their own inbox/sales or inbox/orders page.
    const isDataAvailable =
        currentUser &&
        currentTransaction.id &&
        currentTransaction.id.uuid === params.id &&
        currentTransaction.customer &&
        currentTransaction.provider &&
        /**
         * publicData info of both customer & provider is necessary for tx
         */
        currentTransaction.customer.attributes.profile.publicData &&
        currentTransaction.provider.attributes.profile.publicData &&
        !isLoading &&
        !transactionError;

    const isOwnSale =
        isDataAvailable &&
        isProviderRole &&
        currentUser.id.uuid === currentTransaction.provider.id.uuid;
    const isOwnOrder =
        isDataAvailable &&
        isCustomerRole &&
        currentUser.id.uuid === currentTransaction.customer.id.uuid;

    if (isDataAvailable && isProviderRole && !isOwnSale) {
        // eslint-disable-next-line no-console
        console.error('Tried to access a sale that was not owned by the current user');
        return <NamedRedirect name="InboxPage" params={{ tab: 'sales' }} />;
    } else if (isDataAvailable && isCustomerRole && !isOwnOrder) {
        // eslint-disable-next-line no-console
        console.error('Tried to access an order that was not owned by the current user');
        return <NamedRedirect name="InboxPage" params={{ tab: 'orders' }} />;
    }

    const loadingMessage = isCustomerRole
        ? 'TransactionPage.loadingOrderData'
        : 'TransactionPage.loadingSaleData';

    const initialMessageFailed = !!(
        initialMessageFailedToTransaction &&
        currentTransaction.id &&
        initialMessageFailedToTransaction.uuid === currentTransaction.id.uuid
    );

    const transactionContentClasses = classNames({
        [css.transactionContent]: true,
        [css.transactionContentExpandedDesktop]: !asidePanelVisible,
    });
    const containerRootClassName = classNames(css.sectionContainerRoot, {
        [css.sectionContainerRootConfirmed]: !!emailVerified,
    });
    /**
     * hide tx panel for mob if aside is opened
     */
    const transactionPanelVisibleOnMob = isMobile ? !asidePanelVisible : true;
    const showTransactionPanel =
        transactionPanelVisible && isDetailsPage && isDataAvailable && transactionPanelVisibleOnMob;

    const showAsidePanel = Boolean(
        asidePanelVisible && isDetailsPage && isDataAvailable && currentListingId
    );
    const listingNotFound = transactionError === 'listingNotFound';
    return (
        <Page
            title={intl.formatMessage({ id: 'TransactionPage.title' }, { title: listingTitle })}
            scrollingDisabled={scrollingDisabled}
        >
            <LayoutSingleColumn>
                <LayoutWrapperTopbar
                    className={classNames({
                        [css.staticTopBar]: !isDetailsPage,
                    })}
                >
                    <TopbarContainer
                        currentPage="OrderMessagesPage"
                        showEmailNotificationBar={!isMobile && !computing}
                    />
                </LayoutWrapperTopbar>
                <LayoutWrapperMain className={css.layout}>
                    {isLoading ? (
                        <IconSpinner />
                    ) : (
                        <section className={css.root}>
                            {fetchInProgress && (
                                <p className={css.loading}>
                                    <FormattedMessage id={`${loadingMessage}`} />
                                </p>
                            )}
                            {inboxPanelVisible && (
                                <aside className={css.inboxContent}>
                                    <InboxPanel
                                        containerRootClassName={containerRootClassName}
                                        navSectionClassName={css.navSection}
                                        transactions={transactions}
                                        currentUser={currentUser}
                                        isMobile={isMobile}
                                        allUserMessages={props.allUserMessages}
                                        fetchOrdersOrSalesError={fetchOrdersOrSalesError}
                                        setInboxPanelVisible={setInboxPanelVisible}
                                        archievedTransactionsError={
                                            props.archievedTransactionsError
                                        }
                                        params={params}
                                        appointmentEntities={props.appointmentEntities}
                                        intl={intl}
                                        currentListing={currentListing}
                                        currentTransaction={currentTransaction}
                                        listingNotFound={listingNotFound}
                                    />
                                </aside>
                            )}
                            {((!isLoading && !isDataAvailable) || transactionError) &&
                                transactionPanelVisible && (
                                    <p className={css.infoNotAvailable}>
                                        {transactionError ? (
                                            <FormattedMessage
                                                id={`TransactionPage.${transactionError}`}
                                            />
                                        ) : (
                                            'Diese Konversation ist nicht mehr verfügbar, da das Konto des Mitglieds gelöscht wurde.'
                                        )}
                                    </p>
                                )}
                            {showTransactionPanel && (
                                <main className={transactionContentClasses}>
                                    <TransactionPanel
                                        otherParty={otherParty}
                                        currentUser={currentUser}
                                        initialMessageFailed={initialMessageFailed}
                                        onSubmitSubscriptionRequest={
                                            handleSubmitSubscriptionRequest
                                        }
                                        navSectionClassName={css.navSection}
                                        containerRootClassName={containerRootClassName}
                                        asidePanelVisible={asidePanelVisible}
                                        setAsidePanelVisibility={setAsidePanelVisibility}
                                        setInboxPanelVisible={setInboxPanelVisible}
                                        isMobile={isMobile}
                                        {...rest}
                                        allUserMessages={allUserMessages}
                                        transaction={currentTransaction}
                                        pageId={pageId}
                                        matchingMatrixLoading={matchingMatrixLoading}
                                        matchingScore={matchingScore}
                                        matchingViewerListing={matchingViewerListing}
                                        matchingCurrentPageListing={matchingCurrentPageListing}
                                    />
                                </main>
                            )}
                            {showAsidePanel && (
                                <aside className={css.asideContent}>
                                    <AsidePanel
                                        riderListing={riderListing}
                                        otherParty={otherParty}
                                        navSectionClassName={css.navSection}
                                        containerRootClassName={containerRootClassName}
                                        setAsidePanelVisibility={setAsidePanelVisibility}
                                        transaction={currentTransaction}
                                        currentListing={currentListing}
                                        currentUser={currentUser}
                                        matchingMatrixLoading={matchingMatrixLoading}
                                        matchingScore={matchingScore}
                                        riderListingsRequestInProgress={
                                            riderListingsRequestInProgress
                                        }
                                        matchingViewerListing={matchingViewerListing}
                                        matchingCurrentPageListing={matchingCurrentPageListing}
                                    />
                                </aside>
                            )}
                        </section>
                    )}
                </LayoutWrapperMain>
                <LayoutWrapperFooter className={css.footer}>
                    <footer />
                </LayoutWrapperFooter>
            </LayoutSingleColumn>
        </Page>
    );
};

const { bool, func, oneOf, shape, string, arrayOf, number, object } = PropTypes;

TransactionPageComponent.propTypes = {
    params: shape({ id: string }).isRequired,
    currentUser: propTypes.currentUser,
    acceptSaleError: propTypes.error,
    appointmentError: string,
    scrollingDisabled: bool.isRequired,
    transaction: propTypes.transaction,
    fetchMessagesError: propTypes.error,
    totalMessagePages: number.isRequired,
    oldestMessagePageFetched: number.isRequired,
    allUserMessages: object.isRequired,
    initialMessageFailedToTransaction: propTypes.uuid,
    savePaymentMethodFailed: bool,
    sendMessageError: propTypes.error,
    onSendMessage: func.isRequired,
    timeSlots: arrayOf(propTypes.timeSlot),
    fetchTimeSlotsError: propTypes.error,
    callSetInitialValues: func.isRequired,
    history: shape({
        push: func.isRequired,
    }).isRequired,
    location: shape({
        search: string,
    }).isRequired,

    intl: intlShape.isRequired,
};

const mapStateToProps = state => {
    const { currentUser } = state.user;
    const {
        archievedTransactionsError,
        archievedTransactionsLoading,
        allUserMessages,
        fetchMessagesError,
        fetchMessagesInProgress,
        allUserMessagesError,
        allUserMessagesRequestInProgress,
        totalMessages,
        totalMessagePages,
        oldestMessagePageFetched,
        sendMessageError,
        sendMessageInProgress,
        sendFileInProgress,
        appointmentEntities,
        appointmentError,
        sendAppointmentInProgress,
        appointmentFetchError,
        appointmentFetchInProgress,
    } = state.InboxPage;

    const { fetchInProgress, transactionRefs, fetchOrdersOrSalesError } = state.InboxPage;

    return {
        transactions: getMarketplaceEntities(state, transactionRefs),
        currentUser: ensureCurrentUser(currentUser),
        scrollingDisabled: isScrollingDisabled(state),
        fetchInProgress,
        allUserMessages,
        fetchOrdersOrSalesError,
        archievedTransactionsError,
        archievedTransactionsLoading,
        fetchMessagesError,
        fetchMessagesInProgress,
        allUserMessagesError,
        allUserMessagesRequestInProgress,
        totalMessages,
        totalMessagePages,
        oldestMessagePageFetched,
        sendMessageError,
        sendMessageInProgress,
        sendFileInProgress,
        appointmentEntities,
        appointmentError,
        sendAppointmentInProgress,
        appointmentFetchError,
        appointmentFetchInProgress,
        ...state.TransactionPage,
    };
};

const mapDispatchToProps = dispatch => ({
    onSendMessage: (txId, message) => dispatch(sendMessage(txId, message)),
    callSetInitialValues: (setInitialValues, values) => dispatch(setInitialValues(values)),
});

const TransactionPage = compose(
    withRouter,
    connect(mapStateToProps, mapDispatchToProps),
    injectIntl
)(TransactionPageComponent);

TransactionPage.loadData = loadData;
TransactionPage.setInitialValues = setInitialValues;

export default TransactionPage;
